'use client'

// import { usePathname, useRouter } from 'next/navigation'
import { useRef } from 'react'
import { EventCard } from '../EventCard'
import { EventCardSkeleton } from '../EventCard/Skeleton'
import { Loader2Icon, PlusIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { RevealSection } from '@/components/RevealSection'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { EVENTS_PER_PAGE } from '@/constants/eventsPerPage'
import { useEventsFormProviderContext } from '@/providers/EventsFormProvider'
import { useEventsContext } from '@/providers/EventsProvider'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

const EventGrid = () => {
  const { events, totalDocs, page, setPage, isLoading, searchTerm, view, format } =
    useEventsContext()
  const { lenis } = useLenisContext()
  const { requestCreateEvent } = useEventsFormProviderContext()

  const totalPages = Math.max(1, Math.ceil(totalDocs / EVENTS_PER_PAGE))
  const startIndex = totalDocs === 0 ? 0 : (page - 1) * EVENTS_PER_PAGE
  // The provider fetches the full (capped) result set so the map can show
  // every pin; the grid paginates that set client-side.
  const pagedEvents = events.slice(startIndex, startIndex + EVENTS_PER_PAGE)
  const endIndex = Math.min(startIndex + pagedEvents.length, totalDocs)
  const isPaginationEnabled = totalDocs > EVENTS_PER_PAGE
  const gridRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handlePageChange = (newPage: number) => {
    setPage(Math.max(1, Math.min(newPage, totalPages)))
    const container = containerRef.current
    if (container) {
      lenis?.scrollTo(container, { offset: 0 })
      gridRef.current?.focus()
    }
  }

  const handleSave = (_articleId: number) => {
    // !TODO: Implement save functionality
  }

  return (
    <>
      <div className="flex flex-col gap-4 lg:min-h-200" ref={containerRef}>
        <RevealSection stagger className="flex grow-0 items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="font-body text-sm font-normal text-foreground sm:text-base">
              {isLoading
                ? 'Loading...'
                : `Showing ${endIndex} of ${totalDocs} event${totalDocs !== 1 ? 's' : ''}`}
            </p>
            {isLoading && <Loader2Icon className="size-4 animate-spin text-muted-foreground" />}
          </div>
          <Button
            variant="accent"
            customIcon={<PlusIcon className="size-4" />}
            onClick={requestCreateEvent}
          >
            Create an event
          </Button>
        </RevealSection>

        <motion.div
          key={`${view}-${format}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          ref={gridRef}
          tabIndex={-1}
        >
          <div className={cn('group/articles', 'grid grid-cols-1 gap-4')}>
            <AnimatePresence>
              {isLoading ? (
                <>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <EventCardSkeleton key={index} />
                  ))}
                </>
              ) : (
                <>
                  {pagedEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      handleSave={handleSave}
                      showClassificationBadge={view === 'all'}
                    />
                  ))}
                </>
              )}
            </AnimatePresence>
          </div>
          {!isLoading && totalDocs === 0 && searchTerm && (
            <p className="col-span-2 text-muted-foreground">
              No events found matching &quot;{searchTerm}&quot;
            </p>
          )}
        </motion.div>
      </div>

      {isPaginationEnabled && (
        <Pagination
          numResults={totalDocs}
          resultsPerPage={EVENTS_PER_PAGE}
          currentPage={page}
          onPageChange={handlePageChange}
        />
      )}
    </>
  )
}

export default EventGrid
